import { useState, useEffect } from 'react';
import { unifiedAccountingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Factory, Leaf, RefreshCw } from 'lucide-react';

function ConsistencyCircle({ value, size = 120 }) {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 95 ? '#166534' : value >= 90 ? '#D97706' : '#DC2626';

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--border))" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
      </svg>
      <div className="absolute text-center">
        <span className="text-2xl font-heading font-bold" style={{ color }}>{value}%</span>
        <p className="text-[10px] text-muted-foreground">Consistency</p>
      </div>
    </div>
  );
}

export default function UnifiedAccounting() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reallocating, setReallocating] = useState(false);

  const fetchData = () => {
    unifiedAccountingAPI.getData().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const handleReallocate = async () => {
    setReallocating(true);
    await unifiedAccountingAPI.reallocate();
    fetchData();
    setReallocating(false);
  };

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 bg-muted rounded animate-pulse" /><div className="grid grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}</div></div>;

  return (
    <div data-testid="unified-accounting-page" className="space-y-6">
      <div className="cp-page-header">
        <h1 className="cp-page-title">Unified Carbon Accounting</h1>
        <Button onClick={handleReallocate} disabled={reallocating} variant="outline" data-testid="reallocate-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${reallocating ? 'animate-spin' : ''}`} /> Reallocate Automatically
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 animate-fade-in stagger-1" data-testid="ccf-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Factory className="w-5 h-5 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">CCF (Factory)</p>
          </div>
          <p className="text-3xl font-heading font-bold">{data?.ccf?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">tCO2 total</p>
        </Card>

        <Card className="p-6 animate-fade-in stagger-2" data-testid="total-pcf-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <Leaf className="w-5 h-5 text-accent" />
            </div>
            <p className="text-sm text-muted-foreground">Total PCF * Prod</p>
          </div>
          <p className="text-3xl font-heading font-bold">{data?.total_pcf_production?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground mt-1">tCO2 total</p>
        </Card>

        <Card className="p-6 flex flex-col items-center justify-center animate-fade-in stagger-3" data-testid="consistency-card">
          <ConsistencyCircle value={data?.consistency_score || 0} />
        </Card>
      </div>

      <div className="cp-chart-card animate-fade-in" data-testid="ua-monthly-table">
        <h3 className="text-sm font-heading font-semibold mb-4">Monthly Comparison</h3>
        <div className="cp-table-wrapper">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">CCF (tCO2)</TableHead>
                <TableHead className="text-right">PCF*Prod (tCO2)</TableHead>
                <TableHead className="text-right">Consistency</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.monthly_data?.map((m) => (
                <TableRow key={m.month}>
                  <TableCell className="font-medium">{m.month}</TableCell>
                  <TableCell className="text-right font-mono">{m.ccf?.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono">{m.pcf_production?.toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <span className={`font-mono font-semibold ${m.consistency >= 95 ? 'text-green-600' : m.consistency >= 90 ? 'text-amber-500' : 'text-red-500'}`}>
                      {m.consistency}%
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {(!data?.monthly_data || data.monthly_data.length === 0) && (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No data available</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
